import urlcat from 'urlcat';
import { API } from '../constants/apiEndpoints';

const typeWalletFormat = (type: string) => {
  return type.toLowerCase().replaceAll('_', '-');
};

/**
 * Constructs a complete API URL with path parameters
 * 
 * @param endpoint The API endpoint path with parameters (e.g., '/wallets/:id')
 * @param params The parameters to replace in the path (e.g., { id: '123' }) 
 * @returns The constructed URL
 */
export const createApiUrl = (endpoint: string, params?: Record<string, string | number>): string => {
  return urlcat('', endpoint, params || {});
};

/**
 * Dedicated functions for commonly used API endpoints 
 */
export const apiUrls = {
  // Auth related URLs
  /**
   * Get user profile URL
   */
  userProfile: () =>
    createApiUrl(API.USER.PROFILE),

  /**
   * Get delete account URL
   */
  deleteAccount: () =>
    createApiUrl(API.USER.DELETE_ACCOUNT),

  /**
   * Get login URL
   */
  login: () =>
    createApiUrl(API.AUTH.LOGIN),

  /**
   * Get signup URL
   */
  signup: () =>
    createApiUrl(API.AUTH.SIGN_UP),

  /**
   * Get logout URL
   */
  logout: () =>
    createApiUrl(API.AUTH.LOGOUT),

  /**
   * Get check account exists URL
   */
  checkAccount: () =>
    createApiUrl(API.AUTH.CHECK_ACCOUNT),

  /**
   * Get send signup OTP URL
   */
  sendSignupOtp: () =>
    createApiUrl(API.AUTH.SEND_SIGN_UP_OTP),

  /**
   * Get verify signup OTP URL
   */
  verifySignupOtp: () =>
    createApiUrl(API.AUTH.VERIFY_SIGN_UP_OTP),

  /**
   * Get send password reset OTP URL
   */
  sendPasswordResetOtp: () =>
    createApiUrl(API.AUTH.SEND_OTP_RESET_PASSWORD),

  /**
   * Get verify password reset OTP URL
   */
  verifyPasswordResetOtp: () =>
    createApiUrl(API.AUTH.VERIFY_OTP_RESET_PASSWORD),

  /**
   * Get reset password URL
   */
  resetPassword: () =>
    createApiUrl(API.AUTH.RESET_PASSWORD),

  /**
   * Get change password URL
   */
  changePassword: () =>
    createApiUrl(API.USER.CHANGE_PASSWORD),

  /**
   * Get update user avatar URL
   */
  updateAvatar: () =>
    createApiUrl(API.USER.UPDATE_AVATAR),

  /**
   * Get update user nickname URL
   */
  updateNickname: () =>
    createApiUrl(API.USER.UPDATE_NICKNAME),

  /**
   * Get update user profile URL
   */
  updateUserProfile: () =>
    createApiUrl(API.USER.UPDATE_PROFILE),

  // Wallet related URLs
  // Overview related URLs
  /**
   * Get account balance data URL
   */
  accountBalance: () =>
    createApiUrl(API.DASHBOARD.ACCOUNT_BALANCE),

  /**
   * Get statistics URL
   */
  statistics: () =>
    createApiUrl(API.DASHBOARD.STATISTICS),

  // Wallet related URLs
  /**
   * Get all wallets URL
   */
  wallets: () =>
    createApiUrl(API.WALLETS.BASE),

  /**
   * Get total assets URL
   */
  totalAssets: () =>
    createApiUrl(API.WALLETS.TOTAL_ASSETS),

  /**
   * Get balance fluctuations URL
   * @param type Optional wallet type (BASIC, CREDIT, LOAN)
   */
  balanceFluctuations: (type?: WalletType | 'ALL') => {
    if (!type || type === 'ALL') {
      return createApiUrl(API.WALLETS.BALANCE_FLUCTUATIONS);
    }

    return createApiUrl(API.WALLETS.BALANCE_FLUCTUATIONS, { walletType: type });
  },

  /**
   * Get wallet details URL
   * @param id Wallet ID
   * @param type Wallet type (credit, basic, etc.)
   */
  walletDetail: (id: string, type: string) =>
    createApiUrl(API.WALLETS.DETAIL, { id, type }),

  /**
   * Get wallet by ID URL
   * @param id Wallet ID
   */
  walletById: (id: string) =>
    createApiUrl(API.WALLETS.BY_ID, { id }),

  /**
   * Create installment conversion URL
   * @param id Wallet ID
   */
  createInstallment: (id: string) =>
    createApiUrl(API.WALLETS.INSTALLMENT, { id }),

  /**
   * Update installment conversion URL
   * @param id wallet id
   * @param installmentId installment id 
   * @returns 
   */
  updateInstallment: (id: string, installmentId: string) =>
    createApiUrl(API.WALLETS.UPDATE_INSTALLMENT, { id, installmentId }),

  /**
   * Get wallet statements URL
   * @param id Wallet ID
   * @param type Wallet type
   */
  walletStatements: (id: string, type: string) =>
    createApiUrl(API.WALLETS.STATEMENTS, { id, type }),


  /**
   * Edit wallet URL
   * @param id Wallet ID
   * @param type Wallet type
   */
  editWallet: (id: string, type: string = 'credit') =>
    createApiUrl(API.WALLETS.EDIT, { id, type: typeWalletFormat(type) }),

  /**
   * Archive wallet URL
   * @param id Wallet ID
   * @param type Wallet type
   */
  archiveWallet: (id: string, type: string = 'credit') =>
    createApiUrl(API.WALLETS.ARCHIVED, { id, type: typeWalletFormat(type) }),

  adjustBalance: (id: string, type: string = 'credit') =>
    createApiUrl(API.WALLETS.ADJUST_BALANCE, { id, type: typeWalletFormat(type) }),

  /**
   * Delete wallet URL
   * @param id Wallet ID
   * @param type Wallet type
   */
  deleteWallet: (id: string, type: string = 'credit') =>
    createApiUrl(API.WALLETS.BY_ID, { id, type: typeWalletFormat(type) }),

  /**
   * Get credit statement details URL
   * @param walletId Wallet ID
   * @param statementId Statement ID
   * @param type Wallet type (usually 'credit')
   */
  creditWalletCreditTransactions: (walletId: string, statementId: string) =>
    createApiUrl(API.WALLETS.STATEMENT_DETAIL, { id: walletId, statementId, type: 'credit' }),

  /**
   * Get debit statement details URL
   * @param walletId Wallet ID
   * @param statementId Statement ID
   */
  creditWalletDebitTransactions: (walletId: string, statementId: string) =>
    createApiUrl(API.WALLETS.DEBIT_STATEMENT_DETAIL, { id: walletId, statementId, type: 'credit' }),

  /**
   * Get statement details URL
   * @param walletId Wallet ID
   * @param statementId Statement ID
   */
  creditStatementDetail: (walletId: string, statementId: string) =>
    createApiUrl(`/wallets/credit/statements/${walletId}/${statementId}`),

  // Transaction related URLs
  /**
   * Get all transactions URL with optional query parameters
   * @param params Optional query parameters (nextCursor, walletId, fromDate, toDate)
   */
  transactions: (params?: {
    nextCursor?: string;
    walletId?: string;
    fromDate?: string;
    toDate?: string;
    query?: string;
  }) => {
    const queryString = params
      ? Object.entries(params)
        .filter(([_, value]) => value !== undefined && value !== null)
        .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
        .join('&')
      : '';

    return queryString
      ? `${API.TRANSACTIONS.BASE}?${queryString}`
      : API.TRANSACTIONS.BASE;
  },

  /**
   * Get transaction by ID URL
   * @param id Transaction ID
   */
  transactionById: (id: string) =>
    createApiUrl(API.TRANSACTIONS.BY_ID, { id }),

  /**
   * Update transaction URL (unified endpoint)
   * @param id Transaction ID
   */
  updateTransaction: (id: string) =>
    createApiUrl(API.TRANSACTIONS.UPDATE_BY_ID, { id }),

  /**
   * Get loan transaction details by ID URL
   * @param id Transaction ID
   */
  loanTransactionById: (id: string) =>
    createApiUrl(API.TRANSACTIONS.LOAN_BY_ID, { id }),

  /**
   * Get loan transaction detail by ID URL for edit
   * @param transactionId Transaction ID
   */
  loanTransactionDetailById: (transactionId: string) =>
    createApiUrl(API.TRANSACTIONS.LOAN_DETAIL_BY_ID, { transactionId }),

  borrowingTransactionDetailById: (transactionId: string) =>
    createApiUrl(API.TRANSACTIONS.BORROWING_BY_ID, { transactionId }),

  repaymentTransactionDetailById: (transactionId: string) =>
    createApiUrl(API.TRANSACTIONS.REPAYMENT_BY_ID, { transactionId }),

  clearDebtTransactionDetailById: (transactionId: string) =>
    createApiUrl(API.TRANSACTIONS.CLEAR_DEBT_BY_ID, { transactionId }),

  collectingTransactionDetailById: (transactionId: string) =>
    createApiUrl(API.TRANSACTIONS.COLLECTING_BY_ID, { transactionId }),

  lendingTransactionDetailById: (transactionId: string) =>
    createApiUrl(API.TRANSACTIONS.LENDING_BY_ID, { transactionId }),

  /**
   * Get list loans by wallet ID and loan type
   * @param walletId Wallet ID
   * @param loanType Loan type ('active', 'completed')
   */
  loans: (id: string, loanStatus: string) =>
    createApiUrl(API.WALLETS.LOANS, { id, loanStatus }),

  /**
   * Get borrowing loans URL
   * @param walletId Wallet ID
   * @param loanStatus Loan status (e.g., 'active', 'completed')
   */
  borrowingLoans: (walletId: string, loanStatus: string) =>
    createApiUrl(API.LOANS.BORROWING, { id: walletId, loanStatus }),
  /**
   * Get lending loans URL
   * @param walletId Wallet ID
   * @param loanStatus Loan status (e.g., 'active', 'completed')
   */
  lendingLoans: (walletId: string, loanStatus: string) =>
    createApiUrl(API.LOANS.LENDING, { id: walletId, loanStatus }),

  /**
   * Get clear debt URL
   * @param loanId Loan ID
   */
  clearDebt: (loanId: string) =>
    createApiUrl(API.LOANS.CLEAR_DEBT, { id: loanId }),

  /**
   * Get offset API URL
   * @param id Resource ID to offset
   */
  offset: (id: string) =>
    createApiUrl(API.LOANS.OFFSET, { id }),

  /**
   * Get transaction summarize URL
   */
  transactionSummarize: () =>
    createApiUrl(API.DASHBOARD.TRANSACTION_SUMMARIZE),

  /**
   * Get dashboard transactions URL
   */
  dashboardTransactions: () =>
    createApiUrl(API.DASHBOARD.TRANSACTIONS),
};